import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchCorrections, fetchMembers, fetchTasks } from '../api/client';
import type { AuthState } from '../api/request';
import {
  belTerlihat,
  dapatMemutuskanKoreksi,
  dapatMengelolaTugas,
  labelBel,
  tampilanJumlah,
} from '../lib/bel-pemberitahuan';
import { formatCorrectionType } from '../lib/correction-form';
import {
  ambangAwal,
  jumlahPemberitahuan,
  kalimatBagianGagal,
  ringkasanPemberitahuan,
  tugasSelesaiSejak,
} from '../lib/pemberitahuan';
import { bacaPesanDibaca } from '../lib/penanda-dibaca';
import { namaAnggota, pengajuKoreksi, petaNamaAnggota } from '../lib/pengaju-koreksi';
import {
  bacaTerakhirDilihat,
  majukanTerakhirDilihat,
  tulisTerakhirDilihat,
} from '../lib/terakhir-dilihat';
import { waktuRelatif } from '../lib/waktu-relatif';
import { disablePush, enablePush, getPushState, type PushState } from '../lib/webPush';
import { useSession } from './WorkforceContext';
import { useAsync } from './useAsync';

/**
 * Baris kontrol push perangkat di kaki panel — sama perannya dengan `PushToggleRow` milik CRM.
 * State `unsupported`/`unconfigured` → baris disembunyikan total: menawarkan tombol yang pasti
 * gagal lebih buruk daripada tak menawarkan. `denied` → petunjuk, bukan tombol: izin yang
 * ditolak hanya bisa dibuka lagi dari setelan peramban, bukan dari sini.
 */
function PushToggleBaris({ auth }: { auth: AuthState }): ReactNode {
  const [state, setState] = useState<PushState | 'loading'>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getPushState(auth)
      .then((s) => alive && setState(s))
      .catch(() => alive && setState('unsupported'));
    return () => {
      alive = false;
    };
  }, [auth]);

  if (state === 'loading' || state === 'unsupported' || state === 'unconfigured') return null;

  async function toggle(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (state === 'on') {
        await disablePush(auth);
        setState('off');
      } else {
        await enablePush(auth);
        setState('on');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal mengubah notifikasi.');
      setState(await getPushState(auth).catch(() => 'unsupported'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bel-panel-push">
      {state === 'denied' ? (
        <p className="muted small">
          Izin notifikasi diblokir — buka dari setelan peramban untuk mengaktifkannya kembali.
        </p>
      ) : (
        <button
          type="button"
          className="tautan-kecil"
          onClick={() => void toggle()}
          disabled={busy}
        >
          {busy
            ? 'Memproses…'
            : state === 'on'
              ? 'Matikan notifikasi di HP ini'
              : 'Aktifkan notifikasi di HP ini'}
        </button>
      )}
      {error ? (
        <p className="muted small" style={{ color: 'var(--danger)', marginTop: '0.25rem' }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Sebanyak ini yang muat di panel tanpa menuntut gulir panjang di ponsel. */
const MAKS_BARIS = 6;

/**
 * Lonceng pemberitahuan — MEMBUKA PANEL, bukan berpindah halaman.
 *
 * Sebelumnya lonceng ini sebuah tautan: menekannya membawa manajer ke halaman lain, dan bila
 * kebetulan tidak ada isinya, yang didapat adalah layar kosong sebagai imbalan atas satu ketukan.
 * Bentuk yang benar sudah ada di CRM — daftarnya muncul di tempat, di bawah loncengnya.
 *
 * Membawa dua hal yang sifatnya berbeda; alasan keduanya dihitung berbeda ada di `pemberitahuan.ts`.
 */
export function BelPemberitahuan(): ReactNode {
  const session = useSession();
  const bolehKoreksi = dapatMemutuskanKoreksi(session.me.permissions);
  const bolehTugas = dapatMengelolaTugas(session.me.permissions);
  const [terbuka, setTerbuka] = useState(false);

  /*
   * Hook dipanggil TANPA syarat — memanggilnya di balik `if` melanggar aturan hook dan akan pecah
   * begitu izin seseorang berubah di tengah sesi. Yang disyaratkan isinya: tanpa izin, ia tidak
   * menembak API sama sekali.
   */
  const koreksi = useAsync(
    () => (bolehKoreksi ? fetchCorrections(session.auth, 'PENDING') : Promise.resolve([])),
    [session.auth, bolehKoreksi],
  );
  const tugas = useAsync(
    () => (bolehTugas ? fetchTasks(session.auth, 'completed') : Promise.resolve([])),
    [session.auth, bolehTugas],
  );
  const anggota = useAsync(
    () => (bolehKoreksi || bolehTugas ? fetchMembers(session.auth) : Promise.resolve([])),
    [session.auth, bolehKoreksi, bolehTugas],
  );

  /*
   * Ambang dibekukan saat panel DIBUKA, bukan dibaca ulang tiap render — kalau tidak, menandai
   * "sudah dibaca" akan mengosongkan daftar yang sedang dibaca orangnya, di depan matanya. Dipakai
   * HANYA untuk ISI panel (`baruSelesai` di bawah) — angka lencana punya ambangnya sendiri, lihat
   * `lencanaSejak`.
   */
  const ambang = useRef<string>(bacaTerakhirDilihat() ?? ambangAwal(new Date()));
  useEffect(() => {
    if (terbuka) ambang.current = bacaTerakhirDilihat() ?? ambangAwal(new Date());
  }, [terbuka]);

  /*
   * Ambang KHUSUS lencana (23 Sep 2026) — SENGAJA state, bukan ref seperti di atas, karena
   * lencana justru HARUS bereaksi begitu ambangnya maju, sementara isi panel justru TIDAK BOLEH.
   *
   * Sebelum ini lencana memakai `baruSelesai.length` yang sama dengan isi panel — dibekukan oleh
   * ref yang sama pula. Akibatnya membuka lonceng tidak pernah mengosongkan angkanya sendiri;
   * satu-satunya jalan adalah menekan "Tandai sudah dibaca" secara eksplisit, LALU menutup dan
   * membuka panel sekali lagi. Dilaporkan Owner sebagai "angka penanda tidak langsung hilang".
   *
   * Efek ini SENGAJA dideklarasikan SESUDAH efek pembekuan `ambang` di atas — keduanya berbagi
   * dependensi `[terbuka]`, dan React menjalankan efek pada urutan deklarasinya di commit yang
   * sama. `ambang.current` harus sempat membaca nilai LAMA dari penyimpanan sebelum efek ini
   * menuliskan nilai BARU ke penyimpanan yang sama — kalau urutannya terbalik, daftar yang sedang
   * dibaca orangnya akan ikut kosong, persis cacat yang komentar di atas cegah.
   */
  const [lencanaSejak, setLencanaSejak] = useState<string>(
    () => bacaTerakhirDilihat() ?? ambangAwal(new Date()),
  );
  const { reload: muatUlangKoreksi } = koreksi;
  const { reload: muatUlangTugas } = tugas;
  useEffect(() => {
    if (!terbuka) return;
    const sekarangIso = new Date().toISOString();
    tulisTerakhirDilihat(sekarangIso);
    setLencanaSejak(sekarangIso);
    /*
     * Dilaporkan Owner 29 Sep 2026 — "notif tidak hilang" meski TIDAK ADA lagi yang menunggu.
     * `koreksi`/`tugas` dimuat SEKALI saat AppShell mount (bel ini ikut di dalamnya, hidup
     * sepanjang sesi, tidak pernah unmount lintas halaman) dan tidak punya pemicu muat ulang
     * apa pun sebelumnya — memutuskan koreksi di /attendance/corrections tidak pernah
     * mengabari lonceng, jadi `menunggu.length` tetap angka lama sampai browser di-refresh
     * penuh. Membuka lonceng adalah satu-satunya momen yang masuk akal untuk menanyakan ulang
     * server "apakah masih benar", jadi disambungkan ke efek yang sudah ada di sini.
     */
    muatUlangKoreksi();
    muatUlangTugas();
  }, [terbuka, muatUlangKoreksi, muatUlangTugas]);

  const petaNama = useMemo(
    () => petaNamaAnggota(anggota.state.status === 'ready' ? anggota.state.data : []),
    [anggota.state],
  );
  const baruSelesai = useMemo(
    () =>
      tugas.state.status === 'ready' ? tugasSelesaiSejak(tugas.state.data, ambang.current) : [],
    [tugas.state, terbuka],
  );
  const tugasUntukLencana = useMemo(
    () => (tugas.state.status === 'ready' ? tugasSelesaiSejak(tugas.state.data, lencanaSejak) : []),
    [tugas.state, lencanaSejak],
  );

  const menunggu = koreksi.state.status === 'ready' ? koreksi.state.data : [];
  const memuat = koreksi.state.status === 'loading' || tugas.state.status === 'loading';

  /*
   * Dipakai UNTUK ISI PANEL (pesan "tidak ada pemberitahuan", peringatan bagian gagal) — sengaja
   * tetap dari `baruSelesai` yang dibekukan, BUKAN `tugasUntukLencana`, supaya pesan di dalam
   * panel tidak pernah bertentangan dengan baris yang sedang ditampilkannya sendiri.
   */
  const ringkasan = ringkasanPemberitahuan({
    koreksiGagal: koreksi.state.status === 'error',
    tugasGagal: tugas.state.status === 'error',
    jumlahKoreksi: menunggu.length,
    jumlahTugas: baruSelesai.length,
  });

  /**
   * Dipakai UNTUK LENCANA — pengajuan koreksi TETAP dihitung apa adanya (menunggu keputusan,
   * bukan pertanyaan "sudah dilihat"), tapi tugas selesai memakai `tugasUntukLencana` yang
   * langsung nol begitu panel dibuka. Ini angka yang benar-benar ditampilkan di ikon.
   */
  const jumlahLencana = jumlahPemberitahuan(menunggu.length, tugasUntukLencana.length);

  /*
   * App Badge (11 Sep 2026) — dipanggil TANPA syarat (sebelum `belTerlihat` early-return, sama
   * seperti hook lain di atas — melanggar aturan hook kalau ditaruh sesudahnya). Untuk kurir yang
   * tak punya izin melihat lonceng, `koreksi`/`tugas` di atas sudah `Promise.resolve([])`, jadi
   * `jumlahLencana` alami menjadi 0 dan badge ikut bersih tanpa kasus khusus.
   *
   * BUKAN sumber kebenaran satu-satunya: push (sw.js Workforce) juga menulis badge saat aplikasi
   * TIDAK terbuka, tapi HANYA dari `koreksiMenunggu` — "tugas selesai" tidak punya keadaan di
   * server (lihat komentar `pemberitahuan.ts`), jadi backend tidak bisa menghitungnya untuk push.
   * Efek ini (app terbuka) memakai angka LENGKAP karena sudah dihitung di klien lewat
   * `terakhirDilihat`. Konsekuensi yang diterima sadar: badge dari push closed-app bisa sedikit
   * lebih rendah dari badge saat app dibuka kembali (tugas selesai belum ikut terhitung) — bukan
   * bug, cerminan dari batas yang sama yang sudah didokumentasikan `pemberitahuan.ts`.
   */
  useEffect(() => {
    if (!('setAppBadge' in navigator)) return;
    if (jumlahLencana > 0) {
      void navigator.setAppBadge(jumlahLencana).catch(() => {});
    } else {
      void navigator.clearAppBadge().catch(() => {});
    }
  }, [jumlahLencana]);

  /*
   * Ketukan notifikasi HP saat aplikasi SUDAH terbuka (#1756). Jalur utamanya sw.js menavigasi
   * tab itu ke URL ber-`?dibaca=…` (dimuat ulang penuh, ditangani `main.tsx`). Tetapi
   * `WindowClient.navigate()` menolak tab yang tidak dikendalikan service worker ini — sebelumnya
   * tab seperti itu hanya difokuskan tanpa berpindah dan tanpa jejak. Untuk tab itu sw.js mengirim
   * pesan; di sini penanda dimajukan, lencana langsung disegarkan, lalu berpindah ke tujuan klik.
   */
  const navigate = useNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const sw = navigator.serviceWorker;
    function dengar(event: MessageEvent): void {
      const pesan = bacaPesanDibaca(event.data);
      if (!pesan) return;
      if (pesan.dibacaSampai !== null && majukanTerakhirDilihat(pesan.dibacaSampai)) {
        setLencanaSejak(bacaTerakhirDilihat() ?? ambangAwal(new Date()));
      }
      muatUlangKoreksi();
      muatUlangTugas();
      void navigate(pesan.url);
    }
    sw.addEventListener('message', dengar);
    return () => sw.removeEventListener('message', dengar);
  }, [navigate, muatUlangKoreksi, muatUlangTugas]);

  if (!belTerlihat(session.me.permissions)) return null;

  const lencana = tampilanJumlah(jumlahLencana);
  const label = labelBel(menunggu.length, tugasUntukLencana.length);
  const peringatan = kalimatBagianGagal(ringkasan.bagianGagal);
  const sekarang = new Date();

  function tandaiDibaca(): void {
    const sekarangIso = new Date().toISOString();
    tulisTerakhirDilihat(sekarangIso);
    setLencanaSejak(sekarangIso);
    tugas.reload();
  }

  return (
    <div className="bel-bungkus">
      <button
        type="button"
        className={`bel${jumlahLencana > 0 ? ' bel-ada' : ''}`}
        aria-label={label}
        aria-expanded={terbuka}
        onClick={() => setTerbuka((v) => !v)}
      >
        {/*
          SVG, bukan emoji: emoji berubah bentuk antar-perangkat dan tidak dapat mewarisi warna
          teksnya, sehingga lonceng yang "menyala" tidak dapat dibedakan dari yang diam.
        */}
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
          <path
            d="M12 3a5 5 0 0 0-5 5v3.6l-1.3 2.6a1 1 0 0 0 .9 1.4h10.8a1 1 0 0 0 .9-1.4L17 11.6V8a5 5 0 0 0-5-5Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
          <path
            d="M10 18.5a2 2 0 0 0 4 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </svg>
        {lencana ? <span className="bel-lencana">{lencana}</span> : null}
      </button>

      {terbuka ? (
        <>
          {/*
            Lapisan penutup: satu ketukan di mana pun menutup panel. Tanpa ini, panel di ponsel
            hanya dapat ditutup dengan menekan tepat loncengnya lagi — sasaran 44px di pojok layar,
            dan orang akan menekan tombol lain di belakangnya tanpa sengaja.
          */}
          <button
            type="button"
            className="bel-penutup"
            aria-label="Tutup pemberitahuan"
            onClick={() => setTerbuka(false)}
          />
          <div className="bel-panel" role="dialog" aria-label="Pemberitahuan">
            <div className="bel-panel-kepala">
              <strong>Pemberitahuan</strong>
              {baruSelesai.length > 0 ? (
                <button type="button" className="tautan-kecil" onClick={tandaiDibaca}>
                  Tandai sudah dibaca
                </button>
              ) : null}
            </div>

            {peringatan ? <p className="bel-peringatan">{peringatan}</p> : null}

            <div className="bel-panel-isi">
              {memuat ? <p className="muted small">Memuat…</p> : null}

              {!memuat && ringkasan.kosongSungguhan ? (
                <p className="muted small">
                  Tidak ada pemberitahuan baru. Pengajuan koreksi dan penyelesaian tugas akan muncul
                  di sini.
                </p>
              ) : null}

              {menunggu.slice(0, MAKS_BARIS).map((k) => (
                <Link
                  key={k.id}
                  to="/attendance/corrections"
                  className="bel-baris"
                  onClick={() => setTerbuka(false)}
                >
                  <span className="bel-baris-judul">
                    <strong>{pengajuKoreksi(petaNama, k).subjek}</strong> ·{' '}
                    {formatCorrectionType(k.correctionType)}
                  </span>
                  <span className="muted small">{k.reason}</span>
                  <span className="muted small">{waktuRelatif(k.createdAt, sekarang)}</span>
                </Link>
              ))}

              {baruSelesai.slice(0, MAKS_BARIS).map((t) => (
                <Link
                  key={t.id}
                  to={`/tasks/${t.id}`}
                  className="bel-baris"
                  onClick={() => setTerbuka(false)}
                >
                  <span className="bel-baris-judul">
                    <strong>{t.title}</strong> · selesai
                  </span>
                  <span className="muted small">
                    {t.taskNumber}
                    {t.assigneeUserId ? ` · ${namaAnggota(petaNama, t.assigneeUserId)}` : ''}
                  </span>
                  <span className="muted small">
                    {t.completedAt ? waktuRelatif(t.completedAt, sekarang) : ''}
                  </span>
                </Link>
              ))}
            </div>

            <Link to="/pemberitahuan" className="bel-panel-kaki" onClick={() => setTerbuka(false)}>
              Lihat semua
            </Link>
            <PushToggleBaris auth={session.auth} />
          </div>
        </>
      ) : null}
    </div>
  );
}
